import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PurchaseService, ShoppingCartService } from '@ridango/components';
import {
    AreaCode,
    CategoryProduct,
    CompanionProfile,
    LocalProduct,
    Product,
    ProductCode,
    ProductsInUrl,
    ProductTravellerType,
    SingleTicketGroupTicketTypeLimits,
    ZoneInterface
} from '@ridango/components/lib/purchase/purchase.model';
import { AppStartupService, BaseComponent, TranslateObjectFieldPipe } from '@ridango/core';
import Env from '@ridango/core/env';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { AmountAnnouncerService } from '@ridango/core/lib/services/amount-announcer.service';

@Component({
    selector: 'rwidget-purchase-ticket-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss']
})
export class ProductComponent extends BaseComponent implements OnInit, OnDestroy {
    @Output() nextStep = new EventEmitter<void>();
    @Input() categoryProducts: CategoryProduct[] = [];
    @Input() urls: string[];
    @Input() zone: ZoneInterface;

    constructor(
        private purchaseService: PurchaseService,
        private activatedRoute: ActivatedRoute,
        public appStartupService: AppStartupService,
        private router: Router,
        public translate: TranslateService,
        private shoppingCartService: ShoppingCartService,
        @Inject('ENV') public env: Env,
        private translatePipe: TranslateObjectFieldPipe,
        private amountAnnouncer: AmountAnnouncerService
    ) {
        super();
    }

    public productTravellerTypes: ProductTravellerType[] = [];
    private singleTicketGroupTicketTypeLimits: SingleTicketGroupTicketTypeLimits = {
        total: 0,
        perType: {}
    };
    public currency: string = this.appStartupService.getCurrency();
    public currentLanguage = this.translate.currentLang;
    public isLoading$ = this.purchaseService.isPurchaseDataLoading$;
    public productAreas;

    public isAddButtonDisabled$ = this.purchaseService.ticketCount$.pipe(
        map((count) => count >= this.purchaseService.MAX_TICKETS_AMOUNT)
    );

    public isAddButtonDisabledForType$: Observable<Record<string, boolean>> = combineLatest([
        this.purchaseService.ticketCount$,
        this.purchaseService.productTravellerTypePurchaseCount$
    ]).pipe(
        map(([ticketCount, typeCounts]) => {
            const disabledPerType: Record<string, boolean> = {};
            const isTicketCountExceeded = ticketCount >= this.purchaseService.MAX_TICKETS_AMOUNT;
            if (this.productTravellerTypes?.[0].isSingleTicketGroup) {
                const productIdWithoutParameters = (this.productTravellerTypes?.[0].id as string).split(':')[0];
                const totalSelectedTypesCount = Object.values(typeCounts).reduce((sum, val) => sum + val, 0);

                Object.keys(this.singleTicketGroupTicketTypeLimits.perType).forEach((code) => {
                    const typeKey = `${productIdWithoutParameters}:${code}`;

                    const isDisabled =
                        isTicketCountExceeded ||
                        typeCounts[code] >= this.singleTicketGroupTicketTypeLimits.perType[code] ||
                        totalSelectedTypesCount >= this.singleTicketGroupTicketTypeLimits.total;

                    disabledPerType[typeKey] = isDisabled;
                });
            } else {
                for (const product of this.productTravellerTypes) {
                    disabledPerType[product.id] = isTicketCountExceeded;
                }
            }
            return disabledPerType;
        })
    );

    ngOnInit(): void {
        this.purchaseService
            .getAreas()
            .pipe(
                switchMap((areas) => {
                    const area = areas?.find((a) => a.code === AreaCode.area);

                    return this.purchaseService.getProductAreas(area?.id);
                })
            )
            .subscribe((productAreas) => {
                this.productAreas = productAreas;
            });
        this.initProductValues();
    }

    handleNextClicked() {
        this.nextStep.emit();
    }

    async removeAmount(productId: number) {
        const product = this.productTravellerTypes.find((userProfile) => userProfile.id === productId);
        product.amount--;

        this.purchaseService.reduceLocalTicketTypeAmount(product);
        await this.populateUrl();
        await this.amountAnnouncer.announceAmountAndTotal(product.amount, this.getTotalAmount(), this.currency);
    }

    async addAmount(productTravellerTypeId: number) {
        const product = this.productTravellerTypes.find((travellerType) => travellerType.id === productTravellerTypeId);
        product.amount++;
        const productIdWithoutParameters = (product.id as string).split(':')[0];

        this.purchaseService.increaseLocalProductTravellerTypeAmount({
            ...product,
            id: productIdWithoutParameters
        });
        await this.populateUrl();
        await this.amountAnnouncer.announceAmountAndTotal(product.amount, this.getTotalAmount(), this.currency);
    }

    private getTotalAmount(): number {
        if (!this.productTravellerTypes?.length) return 0;
        return this.productTravellerTypes.reduce((sum, p) => {
            const unit = this.getUnitPrice(p);
            const amount = Number(p.amount) || 0;
            return sum + unit * amount;
        }, 0);
    }

    private getUnitPrice(product: ProductTravellerType): number {
        return product?.price?.amount ?? product?.price?.maxAmount ?? 0;
    }

    private initProductValues(): void {
        this.getCartStatus$()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (cart) => this.handleProductInitialization(cart),
                (error) => this.handleInitializationError(error),
                () => this.purchaseService.setPurchaseDataLoading(false)
            );
    }

    private getCartStatus$() {
        const currentCartId = this.shoppingCartService.getCartId();

        return currentCartId
            ? this.shoppingCartService.getShoppingCartStatus(currentCartId).pipe(catchError(() => of(null)))
            : of(null);
    }

    private handleProductInitialization(cart: any): void {
        const productsInCart = cart?.tickets || [];
        const localProducts = this.purchaseService.getLocalProducts();

        const { urlSegments, selectedProductType, urlSegmentsSubtype } = this.getUrlData();

        const categoryProducts = this.getFilteredCategoryProducts(selectedProductType, urlSegmentsSubtype);

        this.productTravellerTypes = this.buildTicketTypeFields(
            this.purchaseService.getStepDataIdsByStepName('product'),
            categoryProducts,
            productsInCart,
            localProducts
        );

        this.filterProductsByTimeInterval();
        this.filterProductsByCode(selectedProductType, urlSegmentsSubtype);

        this.updateLocalProducts(localProducts);
        this.purchaseService.updateCartTicketCount(productsInCart.length);

        this.navigateWithSelectedProducts(urlSegments);

        this.updateAriaLabels();
        this.purchaseService.setPurchaseDataLoading(false);
    }

    private getUrlData() {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);

        const [, selectedProductType] = urlSegments[1].split(':');

        return {
            urlSegments,
            selectedProductType,
            urlSegmentsSubtype: urlSegments[2]
        };
    }

    private getFilteredCategoryProducts(selectedProductType: string, urlSegmentsSubtype: string) {
        const categoryProductId = this.purchaseService.getStepDataIdsByStepName('category-product')[0];

        const products = this.categoryProducts.find((c) => c.id === categoryProductId)?.products || [];

        return selectedProductType === ProductCode.SINGLE_TICKET_GROUP
            ? products.filter((p) => p.id === urlSegmentsSubtype)
            : products;
    }

    private filterProductsByTimeInterval(): void {
        const categoryProductIds = this.purchaseService.getStepDataIdsByStepName('category-product');

        const intervalValue = categoryProductIds[1];

        if (!intervalValue?.includes('timeInterval')) {
            return;
        }

        const typeInterval = intervalValue.includes('24') ? '24' : '30';

        this.productTravellerTypes = this.productTravellerTypes.filter((product) =>
            product.name.translations[0].translation.includes(typeInterval)
        );
    }

    private filterProductsByCode(selectedProductType: string, urlSegmentsSubtype: string): void {
        if (selectedProductType === ProductCode.SINGLE_TICKET_GROUP) {
            return;
        }

        const [, code] = urlSegmentsSubtype.split(':');

        if (!this.env.PURCHASE_FLOW?.SORTING_BY_CODE || !code) {
            return;
        }

        this.productTravellerTypes = this.productTravellerTypes.filter((product: Product) =>
            product.usageValidityPeriods.some((validityPeriod) => validityPeriod.code === code)
        );
    }

    private updateLocalProducts(localProducts: Product[]): void {
        this.purchaseService.setArrayLocalProducts(
            this.productTravellerTypes.filter((product) => localProducts.some((item) => item.id === product.id))
        );
    }

    private navigateWithSelectedProducts(urlSegments: string[]): void {
        urlSegments.splice(urlSegments.indexOf('product') + 1);

        this.router.navigate(
            [
                this.translate.currentLang,
                ...this.urls,
                ...urlSegments,
                ...this.productTravellerTypes
                    .filter((item) => item.amount > 0)
                    .reduce((acc, item) => [...acc, item.id, item.amount], [])
            ],
            {
                replaceUrl: true
            }
        );
    }

    private handleInitializationError(error: unknown): void {
        console.error(error);
    }

    private updateAriaLabels(): void {
        if (!this.productTravellerTypes?.length) return;

        this.productTravellerTypes = this.productTravellerTypes.map((ptt) => {
            const ariaLabel = this.amountAnnouncer.buildProductAriaLabel({
                name: this.translatePipe.transform(ptt?.name, null, this.currentLanguage) || '',
                description: this.getProductDescription(ptt), // Using your existing helper
                amount: this.amountAnnouncer.resolveAmountValue(ptt?.price),
                priceCurrency: ptt?.price?.currency,
                fallbackCurrency: this.currency
            });

            return { ...ptt, ariaLabel };
        });
    }
    private buildTicketTypeFields(
        productsInUrl: ProductsInUrl,
        foundProductsInCategory,
        productsInCart,
        localProducts: LocalProduct[]
    ): Product[] {
        if (foundProductsInCategory.length > 0 && foundProductsInCategory[0].userProfiles?.[0]?.isGroup) {
            return this.buildTravellerTypeFieldsFromParameters(
                productsInUrl,
                foundProductsInCategory[0],
                productsInCart,
                localProducts
            );
        } else {
            return this.buildTravellerTypeFieldsFromProducts(
                productsInUrl,
                foundProductsInCategory,
                productsInCart,
                localProducts
            );
        }
    }

    private buildTravellerTypeFieldsFromProducts(
        productIdsInUrl,
        foundProductsInCategory,
        productsInCart,
        localProducts
    ) {
        return foundProductsInCategory.map((product) => {
            const amount = this.getSelectedTravellerTypeAmount(
                product,
                productsInCart,
                localProducts,
                product.id,
                productIdsInUrl
            );
            return { ...product, amount };
        });
    }

    private buildTravellerTypeFieldsFromParameters(
        productIdsInUrl: ProductsInUrl,
        product: Product,
        productsInCart,
        localProducts: LocalProduct[]
    ): Product[] {
        this.singleTicketGroupTicketTypeLimits.total = product.userProfiles[0].maxNumberOfPersons;
        return product.userProfiles[0].companionProfiles?.map((companionProfile: CompanionProfile) => {
            const productTravellerTypeParameterCode = companionProfile.dynamicProductParameters[0].code;
            const productTravellerTypeId = `${product.id}:${productTravellerTypeParameterCode}`;
            const amount = this.getSelectedTravellerTypeAmount(
                product,
                productsInCart,
                localProducts,
                productTravellerTypeId,
                productIdsInUrl
            );

            this.singleTicketGroupTicketTypeLimits.perType[productTravellerTypeParameterCode] =
                companionProfile.maxNumberOfPersons;

            return {
                ...product,
                isSingleTicketGroup: true,
                id: productTravellerTypeId,
                productTravellerTypeParameterCode,
                name: companionProfile.userProfile.name,
                descriptionValue:
                    this.purchaseService.getProductDescription(this.productAreas, product) +
                    this.translatePipe.transform(companionProfile.userProfile.name, null, this.translate.currentLang),
                amount,
                price: this.purchaseService.defineProductPriceByActivationMethod({
                    ...product,
                    price: companionProfile.perPersonPrice
                })
            };
        });
    }

    private getSelectedTravellerTypeAmount(
        product,
        productsInCart,
        localProducts,
        productTravellerTypeId,
        productIdsInUrl
    ): number {
        const indexUrlProduct = productIdsInUrl.indexOf(productTravellerTypeId);
        let amount = 0;
        if (
            productsInCart.find(
                (productCart) =>
                    productCart.productId === product.id &&
                    product.startZoneId === productCart.startZoneId &&
                    product.endZoneId === productCart.endZoneId
            ) ||
            localProducts.find(
                (localProduct) =>
                    localProduct.id === productTravellerTypeId &&
                    product.startZoneId === localProduct.startZoneId &&
                    product.endZoneId === localProduct.endZoneId
            )
        ) {
            amount =
                productsInCart.filter(
                    (productCart) =>
                        productCart.productId === product.id && product.startZoneId === productCart.startZoneId
                ).length +
                (localProducts.find(
                    (localProduct) =>
                        localProduct.id == productTravellerTypeId &&
                        product.startZoneId === localProduct.startZoneId &&
                        product.endZoneId === localProduct.endZoneId
                )?.amount || 0);
        } else {
            amount = indexUrlProduct !== -1 ? +productIdsInUrl[indexUrlProduct + 1] : 0;
        }

        return amount;
    }

    private async populateUrl() {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        urlSegments.splice(urlSegments.indexOf('product') + 1);

        const filteredProducts = [...this.productTravellerTypes].filter((product) => product.amount > 0);
        const productsRoute = filteredProducts.map((item) => `${item.id}/${item.amount}`).join('/');
        await this.router.navigate(
            [this.translate.currentLang, ...this.urls, ...urlSegments, ...productsRoute.split('/')],
            { replaceUrl: true }
        );
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    getProductDescription(product) {
        return this.purchaseService.getProductDescription(this.productAreas, product);
    }
}
